import type { TColumn } from '@features/ui/components/table';

import { FC, memo, useCallback, useDeferredValue, useState } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  periodOptions,
  groupByOptions,
  productTypeOptions,
} from '@features/admin/reports/reportData';
import { toast } from 'react-toastify';
import {
  ReportPeriod,
  ReportGroupBy,
  TReportPaymentsMinusRefundsByCustomerCountryParams,
  TReportPaymentsMinusRefundsByCustomerCountryData,
} from '@src/types/reports';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { useReportChartData, getChartColor } from '@hooks/useReportChartData';
import {
  exportPaymentsMinusRefundsByCustomerCountry,
  getPaymentsMinusRefundsByCustomerCountry,
} from '@src/services/reports';
import dayjs from 'dayjs';
import { downloadFile } from '@src/lib/utils';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

type PaymentsMinusRefundsByCustomerCountryTableProps = {
  columns: TColumn<TReportPaymentsMinusRefundsByCustomerCountryData>[];
  data: TReportPaymentsMinusRefundsByCustomerCountryData[];
  onExportReport: (format: 'csv' | 'xlsx') => void;
};

const PaymentsMinusRefundsByCustomerCountryTable = memo(
  ({
    columns,
    data,
    onExportReport,
  }: PaymentsMinusRefundsByCustomerCountryTableProps) => {
    return (
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-white">Detailed Data</h2>
        <RadixTable columns={columns} data={data} />
        <Divider className="my-4" />
        <div className="flex flex-wrap justify-end gap-3">
          <Button
            className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
            variant="outline"
            onClick={() => onExportReport('csv')}
          >
            Download CSV
          </Button>
          <Button
            className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
            variant="outline"
            onClick={() => onExportReport('xlsx')}
          >
            Download XLSX
          </Button>
        </div>
      </div>
    );
  }
);

PaymentsMinusRefundsByCustomerCountryTable.displayName =
  'PaymentsMinusRefundsByCustomerCountryTable';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    dataKey: string;
    color: string;
    payload: any;
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="bg-card-stat p-2.5 rounded-lg text-xs border border-[#3D3D3D] flex flex-col gap-y-1 min-w-[200px]"
        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
      >
        <p className="text-[#999999] text-[10px] text-center mb-1">
          {payload[0]?.payload?.date}
        </p>
        {payload.map((entry, index) => (
          <div key={index} className="flex justify-between items-center gap-4">
            <span className="text-gray-300 text-xs">{entry.dataKey}:</span>
            <span
              className="text-white font-semibold"
              style={{ color: entry.color }}
            >
              ${entry.value.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

export const PaymentsMinusRefundsByCustomerCountry: FC = () => {
  const [data, setData] = useState<
    TReportPaymentsMinusRefundsByCustomerCountryData[]
  >([]);

  const getAccessField = useCallback(
    (item: TReportPaymentsMinusRefundsByCustomerCountryData) => item.date,
    []
  );
  const getMainDataField = useCallback(
    (item: TReportPaymentsMinusRefundsByCustomerCountryData, key: string) =>
      item.countries[key] || 0,
    []
  );
  const getPreviousPeriodField = useCallback(
    (item: TReportPaymentsMinusRefundsByCustomerCountryData, key: string) =>
      item.countries_previous?.[key],
    []
  );
  const getPreviousYearField = useCallback(
    (item: TReportPaymentsMinusRefundsByCustomerCountryData, key: string) =>
      item.countries_previous_year?.[key],
    []
  );

  // Use custom hooks for filter management
  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.LAST_7_DAYS,
    defaultGroupBy: ReportGroupBy.DAY,
    enableProducts: true,
    enableComparisons: true,
  });

  // Use custom hook for table columns generation
  const { columns, displayItems, tableData } = useReportTableColumns({
    data,
    selectedItems: [],
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField,
    getMainDataField,
    getPreviousPeriodField,
    getPreviousYearField,
    itemsField: 'countries',
    valueFormatter: (_, value: number) => `$${value.toLocaleString()}`,
    includeTotalsRow: true,
  });

  const deferredTableData = useDeferredValue(tableData);

  // Use custom hook for chart data transformation
  const chartData = useReportChartData({
    data,
    displayItems,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, key) => item.countries[key] || 0,
    getPreviousPeriodField: (item, key) => item.countries_previous?.[key],
    getPreviousYearField: (item, key) => item.countries_previous_year?.[key],
  });

  const buildPayload =
    (): TReportPaymentsMinusRefundsByCustomerCountryParams => {
      const productIds = filters.selectedProducts
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value));

      return {
        start_date: filters.startDate
          ? dayjs(filters.startDate).format('YYYY-MM-DD')
          : null,
        end_date: filters.endDate
          ? dayjs(filters.endDate).format('YYYY-MM-DD')
          : null,
        group: filters.selectedGroupBy.toLowerCase() as ReportGroupBy,
        product_ids: productIds.length > 0 ? productIds : undefined,
        compare_to_previous_period: filters.compareToPreviousPeriod,
        compare_to_same_period_previous_year:
          filters.compareToSamePeriodPreviousYear,
      };
    };

  const handleRunReport = useCallback(async () => {
    filters.setIsLoading(true);
    try {
      const payload = buildPayload();
      const response = await getPaymentsMinusRefundsByCustomerCountry(payload);

      setData(response.data);
      toast.success('Report generated successfully!');
    } catch (error) {
      toast.error('Error fetching report');
      console.error('Error fetching report:', error);
    } finally {
      filters.setIsLoading(false);
    }
  }, [buildPayload, filters]);

  const downloadCsvFile = useCallback(
    (blobPart: BlobPart, fileName: string) => {
      const csvBlob = new Blob([blobPart], { type: 'text/csv;charset=utf-8;' });
      const csvUrl = URL.createObjectURL(csvBlob);
      const link = document.createElement('a');

      link.href = csvUrl;
      link.download = `${fileName}.csv`;
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(csvUrl);
      document.body.removeChild(link);
    },
    []
  );

  const handleExportReport = useCallback(
    async (format: 'csv' | 'xlsx') => {
      try {
        const payload = buildPayload();
        const response = await exportPaymentsMinusRefundsByCustomerCountry(
          payload,
          format
        );

        if (format === 'csv') {
          downloadCsvFile(
            response.data,
            'payments_minus_refunds_by_customer_country'
          );
        } else {
          downloadFile(
            response.data,
            'payments_minus_refunds_by_customer_country'
          );
        }

        toast.success('Report exported successfully!');
      } catch (error) {
        console.error('Error exporting report:', error);
        toast.error('Failed to export report');
      }
    },
    [buildPayload, downloadCsvFile]
  );

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Payments Minus Refunds by Customer Country
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          compareToPreviousPeriod={filters.compareToPreviousPeriod}
          compareToSamePeriodPreviousYear={
            filters.compareToSamePeriodPreviousYear
          }
          dateRangeValue={filters.valueStart}
          groupByOptions={groupByOptions}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          productOptions={productTypeOptions}
          selectedGroupBy={filters.selectedGroupBy}
          selectedPeriod={filters.selectedPeriod}
          selectedProducts={filters.selectedProducts}
          showCompareToPreviousPeriod={filters.showPreviousPeriodCheckbox}
          showCompareToSamePeriodPreviousYear={filters.showPreviousYearCheckbox}
          showProductsFilter={true}
          onCompareToPreviousPeriodChange={filters.setCompareToPreviousPeriod}
          onCompareToSamePeriodPreviousYearChange={
            filters.setCompareToSamePeriodPreviousYear
          }
          onDateRangeChange={filters.setValueStart}
          onGroupByChange={filters.setSelectedGroupBy}
          onPeriodChange={filters.setSelectedPeriod}
          onProductsChange={filters.setSelectedProducts}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `$${value.toLocaleString()}`}
              />
              <Tooltip content={<CustomTooltip />} />
              {/* Main data lines */}
              {displayItems.map((country, index) => {
                return (
                  <Line
                    key={country}
                    activeDot={{ r: 6 }}
                    dataKey={country}
                    dot={{ fill: getChartColor(index), r: 4 }}
                    name={country}
                    stroke={getChartColor(index)}
                    strokeWidth={2}
                    type="monotone"
                  />
                );
              })}

              {/* Previous period lines */}
              {filters.compareToPreviousPeriod &&
                displayItems.map((country, index) => {
                  return (
                    <Line
                      key={`${country}_previous_period`}
                      activeDot={{ r: 4 }}
                      dataKey={`${country}_previous_period`}
                      dot={{
                        fill: getChartColor(index),
                        r: 3,
                      }}
                      name={`${country} (Previous Period)`}
                      stroke={getChartColor(index)}
                      strokeDasharray="5 5"
                      strokeWidth={1.5}
                      type="monotone"
                    />
                  );
                })}

              {/* Previous year lines */}
              {filters.compareToSamePeriodPreviousYear &&
                displayItems.map((country, index) => {
                  return (
                    <Line
                      key={`${country}_previous_year`}
                      activeDot={{ r: 4 }}
                      dataKey={`${country}_previous_year`}
                      dot={{
                        fill: getChartColor(index),
                        r: 3,
                      }}
                      name={`${country} (Previous Year)`}
                      stroke={getChartColor(index)}
                      strokeDasharray="2 2"
                      strokeWidth={1.5}
                      type="monotone"
                    />
                  );
                })}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <PaymentsMinusRefundsByCustomerCountryTable
          columns={columns}
          data={deferredTableData}
          onExportReport={handleExportReport}
        />
      </Card>
    </div>
  );
};
